/* eslint-disable @typescript-eslint/no-redeclare */
import { PeriodContext } from '../../context/PeriodProvider';
import { ConsumptionClient } from '../../services/ConsumptionClient';
import { faArrowDownLong, faArrowUpLong, faChartSimple } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { Skeleton } from '../ui/Skeleton';
import { UserContext } from '../../context/UserProvider';
import { Measurement } from '../../types/Measurement';
import { useParams } from 'react-router-dom';
import { Message } from '../ui/Message';
import { Card } from '../ui/Card';
import { buildQueryKey } from '../../lib/utils';

export const ConsumptionQueryKey = 'consumption';

export const Consumption = () => {
    const { period } = useContext(PeriodContext);
    const user = useContext(UserContext);
    const { id } = useParams();
    const { data, isPending, isError } = useQuery({
        queryKey: [buildQueryKey(ConsumptionQueryKey, id), period],
        queryFn: () => ConsumptionClient({ ...user, param: id }).getConsumption(period),
    });

    if (isError) {
        return (
            <Card>
                <Message type="error">Er is iets misgegaan bij het ophalen van uw verbruik. Probeer het later nog een keer.</Message>
            </Card>
        );
    }

    if (isPending) {
        return (
            <div className="flex w-full gap-4">
                <div className="flex flex-1 flex-col gap-4 items-">
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <div className="flex justify-evenly">
                        <Skeleton className="h-20 w-20 rounded-full" />
                        <Skeleton className="h-20 w-20 rounded-full" />
                    </div>
                </div>
                <div className="flex flex-1 flex-col gap-4 items-">
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <div className="flex justify-evenly">
                        <Skeleton className="h-20 w-20 rounded-full" />
                        <Skeleton className="h-20 w-20 rounded-full" />
                    </div>
                </div>
            </div>
        );
    }

    if (!data) return <Message type="info">Geen data gevonden.</Message>;

    const renderMeasurement = (measurement?: Measurement) => {
        return (
            <>
                <div className="text-xl leading-none">{measurement?.amount ? measurement.amount : 0}</div>
                {measurement?.unit && <div className="text-xs text-black">{measurement.unit}</div>}
            </>
        );
    };

    return (
        <>
            <div className="flex flex-col flex-1 gap-4">
                <div className="text-primary text-center">CO2 uitstoot</div>
                <div className="flex justify-evenly">
                    <div className="flex flex-col items-center gap-1">
                        <div className="flex gap-1 items-center">
                            <FontAwesomeIcon icon={faChartSimple} className="text-xs md:text-sm text-black" />
                            <div className="text-xxs md:text-xs text-black mr-2">Totaal</div>
                        </div>
                        <div className="flex flex-col justify-center items-center rounded-full w-16 h-16 md:h-20 md:w-20 lg:h-16 lg:w-16 xl:h-20 xl:w-20 bg-white">
                            {renderMeasurement(data.carbonEmissionTotal)}
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                        <div className="flex gap-1 items-center">
                            {data.carbonEmissionSaved?.amount !== undefined && (
                                <>
                                    {data.carbonEmissionSaved.amount <= 0 ? (
                                        <FontAwesomeIcon icon={faArrowUpLong} className="text-xs md:text-sm text-red-700" />
                                    ) : (
                                        <FontAwesomeIcon icon={faArrowDownLong} className="text-xs md:text-sm" />
                                    )}
                                </>
                            )}
                            <div className="text-xxs md:text-xs text-black">Bespaard</div>
                        </div>
                        <div className="flex flex-col justify-center items-center rounded-full w-16 h-16 md:h-20 md:w-20 lg:h-16 lg:w-16 xl:h-20 xl:w-20 bg-white">
                            {renderMeasurement(data.carbonEmissionSaved)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col flex-1 gap-4">
                <div className="text-primary text-center">Kilometers per liter</div>
                <div className="flex justify-evenly">
                    <div className="flex flex-col items-center gap-1">
                        <div className="flex gap-1 items-center">
                            <FontAwesomeIcon icon={faChartSimple} className="text-xs md:text-sm text-black" />
                            <div className="text-xxs md:text-xs text-black mr-2">Norm</div>
                        </div>
                        <div className="flex flex-col justify-center items-center rounded-full w-16 h-16 md:h-20 md:w-20 lg:h-16 lg:w-16 xl:h-20 xl:w-20 bg-secondary">
                            {renderMeasurement(data.fuelConsumptionNorm)}
                        </div>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                        <div className="flex gap-1 items-center">
                            {data.fuelConsumptionNorm?.amount !== undefined && data.fuelConsumption?.amount !== undefined && (
                                <>
                                    {data.fuelConsumption.amount < data.fuelConsumptionNorm.amount ? (
                                        <FontAwesomeIcon icon={faArrowUpLong} className="text-xs md:text-sm text-red-700" />
                                    ) : (
                                        <FontAwesomeIcon icon={faArrowDownLong} className="text-xs md:text-sm" />
                                    )}
                                </>
                            )}
                            <div className="text-xxs md:text-xs text-black">Jouw verbruik</div>
                        </div>
                        <div className="flex flex-col justify-center items-center rounded-full w-16 h-16 md:h-20 md:w-20 lg:h-16 lg:w-16 xl:h-20 xl:w-20 bg-secondary">
                            {renderMeasurement(data.fuelConsumption)}
                        </div>
                    </div>
                </div>
            </div>

            <div className="absolute top-0 left-0 bottom-0 w-1/2 bg-secondary -z-10"></div>
        </>
    );
};
